<script setup lang="ts">
import { useAuthStore } from '@/modules/auth/store/auth';
import { storeToRefs } from 'pinia';

const { getUser } = storeToRefs(useAuthStore());
const locationName = computed(() => {
  const u = getUser.value;
  return u?.location_name ?? u?.account?.business_name ?? 'Dashboard';
});

const clock = ref('');
const clockTimer = shallowRef<ReturnType<typeof setInterval> | null>(null);

const updateClock = () => {
  const now = new Date();
  clock.value = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
};

onMounted(() => {
  updateClock();
  clockTimer.value = setInterval(updateClock, 10_000);
});

onUnmounted(() => {
  if (clockTimer.value) clearInterval(clockTimer.value);
});

const isConnected = useState<boolean>('merchantIsConnected', () => true);
const connectionLost = computed(() => !isConnected.value);

// Stats bar — left zone: live action/awareness; right zone: day KPIs (placeholder until API ships).
const newCount = useState<number>('merchantNewCount', () => 0);
const scheduledCount = useState<number>('merchantScheduledCount', () => 0);
const scheduledNext = useState<string>('merchantScheduledNext', () => '');
const completedCount = useState<number>('merchantCompletedCount', () => 0);
const todayDisplay = useState<string>('merchantTodayDisplay', () => '—');
const revenueDisplay = useState<string>('merchantRevenueDisplay', () => '—');
const completedPanelOpen = useState<boolean>('merchantCompletedPanelOpen', () => false);
const soundSettingsOpen = useState<boolean>('merchantSoundSettingsOpen', () => false);

// ── Order pause toggle ─────────────────────────────────────────────────────
const { acceptingOrders, loading: statusLoading, toggleError, fetchStatus, toggleStatus } = useMerchantStatus();

// Bug 9 fix: auto-clear toggle error after 4 seconds
let _toggleErrTimer: ReturnType<typeof setTimeout> | null = null;
watch(toggleError, (val) => {
  if (_toggleErrTimer) clearTimeout(_toggleErrTimer);
  if (val) {
    _toggleErrTimer = setTimeout(() => { toggleError.value = null; }, 4000);
  }
});

onMounted(() => {
  fetchStatus();
});
</script>

<template>
  <div class="merchant-layout">
    <div v-if="connectionLost" class="merchant-connection-banner">
      <v-icon icon="mdi-alert-outline" size="18" />
      <span>Connection lost — reconnecting…</span>
    </div>
    <div v-if="!acceptingOrders" class="merchant-paused-banner">
      <v-icon icon="mdi-pause-octagon-outline" size="18" />
      <span>Orders paused — customers cannot place new orders</span>
    </div>

    <header class="merchant-topbar">
      <div class="merchant-topbar__logo">
        <img src="/logo.png" alt="Tradel" height="32" />
      </div>
      <div class="merchant-topbar__location">{{ locationName }}</div>
      <div class="merchant-topbar__right">
        <!-- Bug 9 fix: show error feedback near toggle button -->
        <transition name="toggle-err-fade">
          <span v-if="toggleError" class="merchant-topbar__toggle-error">{{ toggleError }}</span>
        </transition>
        <button
          class="merchant-topbar__toggle"
          :class="acceptingOrders ? 'merchant-topbar__toggle--open' : 'merchant-topbar__toggle--paused'"
          :disabled="statusLoading"
          @click="toggleStatus"
        >
          <template v-if="acceptingOrders">
            <v-icon icon="mdi-check" size="16" />
            <span>Open</span>
          </template>
          <template v-else>
            <v-icon icon="mdi-pause-octagon-outline" size="16" />
            <span>Paused</span>
          </template>
        </button>
        <span class="merchant-topbar__clock">{{ clock }}</span>
        <span
          class="merchant-topbar__dot"
          :class="isConnected ? 'merchant-topbar__dot--live' : 'merchant-topbar__dot--dead'"
        />
        <span class="merchant-topbar__status-label">{{ isConnected ? 'LIVE' : 'OFFLINE' }}</span>
      </div>
    </header>

    <main class="merchant-main">
      <NuxtPage :page-key="(route) => route.fullPath" />
    </main>

    <footer class="merchant-statsbar">
      <!-- Left zone: live action / awareness -->
      <div class="merchant-statsbar__stats">
        <div
          class="merchant-statsbar__stat merchant-statsbar__stat--new"
          :class="{ 'merchant-statsbar__stat--alert': newCount > 0 }"
        >
          <span class="merchant-statsbar__label">New</span>
          <span class="merchant-statsbar__value">
            <span class="merchant-statsbar__dot" />
            {{ newCount }}
          </span>
        </div>

        <div class="merchant-statsbar__stat merchant-statsbar__stat--scheduled">
          <span class="merchant-statsbar__label">Scheduled</span>
          <span class="merchant-statsbar__value">
            <v-icon icon="mdi-calendar-clock" size="16" class="merchant-statsbar__icon--scheduled" />
            {{ scheduledCount }}
            <em v-if="scheduledNext">· next {{ scheduledNext }}</em>
          </span>
        </div>

        <button
          class="merchant-statsbar__stat merchant-statsbar__stat--button merchant-statsbar__stat--completed"
          type="button"
          :aria-expanded="completedPanelOpen"
          aria-controls="merchant-completed-orders-panel"
          @click="completedPanelOpen = !completedPanelOpen"
        >
          <span class="merchant-statsbar__label">Completed</span>
          <span class="merchant-statsbar__value">
            <v-icon icon="mdi-check-circle" size="16" class="merchant-statsbar__icon--completed" />
            {{ completedCount }}
            <v-icon :icon="completedPanelOpen ? 'mdi-chevron-down' : 'mdi-chevron-up'" size="16" />
          </span>
        </button>
      </div>

      <!-- Right zone: day KPIs -->
      <div class="merchant-statsbar__stats merchant-statsbar__stats--kpi">
        <div class="merchant-statsbar__stat">
          <span class="merchant-statsbar__label">Today</span>
          <span class="merchant-statsbar__value">{{ todayDisplay }} <em>placed</em></span>
        </div>

        <div class="merchant-statsbar__stat">
          <span class="merchant-statsbar__label">Revenue</span>
          <span class="merchant-statsbar__value">{{ revenueDisplay }} <em>today</em></span>
        </div>

        <button
          class="merchant-statsbar__settings-toggle"
          type="button"
          aria-label="Order board settings"
          @click="soundSettingsOpen = true; completedPanelOpen = false"
        >
          <v-icon icon="mdi-cog-outline" size="20" />
          <span>Settings</span>
        </button>
      </div>
    </footer>
  </div>
</template>

<style lang="scss">
.merchant-layout * {
  -webkit-tap-highlight-color: transparent;
}
@media (hover: none) {
  .merchant-layout *:hover {
    background-color: inherit !important;
    color: inherit !important;
  }
}
</style>

<style lang="scss" scoped>
$merchant-bg: #1a1a1a;
$tradel-orange: #ff8d2f;
$text-primary: #ffffff;
$text-muted: #9ca3af;

.merchant-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: $merchant-bg;
  color: $text-primary;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.merchant-connection-banner {
  background: #ef4444;
  color: #fff;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  position: sticky;
  top: 0;
  z-index: 1000;
}

.merchant-paused-banner {
  background: rgba(239, 68, 68, 0.15);
  border-bottom: 2px solid #ef4444;
  color: #fca5a5;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  padding: 10px 16px;
  letter-spacing: 0.02em;
}

.merchant-topbar {
  height: 60px;
  background: #111;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;

  &__logo {
    flex: 1;
    display: flex;
    align-items: center;
    img {
      object-fit: contain;
    }
  }

  &__location {
    font-size: 17px;
    font-weight: 600;
    color: $text-primary;
    text-align: center;
    flex: 2;
  }

  &__right {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }

  &__clock {
    font-size: 16px;
    font-weight: 500;
    color: $text-primary;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;

    &--live {
      background: #22c55e;
      box-shadow: 0 0 6px #22c55e;
    }
    &--dead {
      background: #ef4444;
    }
  }

  &__status-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: $text-muted;
  }

  &__toggle {
    min-height: 44px;
    min-width: 90px;
    border-radius: 22px;
    border: none;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.04em;
    cursor: pointer;
    padding: 0 16px;
    transition: opacity 0.15s;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--open {
      background: #22c55e;
      color: #fff;
    }

    &--paused {
      background: #ef4444;
      color: #fff;
      animation: paused-pulse 1.5s ease-in-out infinite;
    }
  }
}

@keyframes paused-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.75; }
}

@keyframes statsbar-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.25); }
}

// Bug 9 fix: toggle error message styling
.merchant-topbar__toggle-error {
  font-size: 12px;
  font-weight: 600;
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.18);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  padding: 4px 10px;
  max-width: 200px;
  line-height: 1.3;
  text-align: center;
}

.toggle-err-fade-enter-active,
.toggle-err-fade-leave-active {
  transition: opacity 0.3s ease;
}
.toggle-err-fade-enter-from,
.toggle-err-fade-leave-to {
  opacity: 0;
}

.merchant-main {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.merchant-statsbar {
  height: 64px;
  background: #111;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 20px;
  position: sticky;
  bottom: 0;
  z-index: 100;

  // ── Stat groups (left zone + right KPI zone) ──────────────────────────
  &__stats {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  // Right zone is visually separated as "day KPIs"
  &__stats--kpi {
    padding-left: 8px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
  }

  &__stat {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    padding: 0 18px;
    min-width: 92px;
    text-align: left;
    border: none;
    background: transparent;
    font: inherit;

    // vertical hairline between stat blocks
    & + & {
      border-left: 1px solid rgba(255, 255, 255, 0.06);
    }
  }

  &__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $text-muted;
    line-height: 1;
  }

  &__value {
    font-size: 18px;
    font-weight: 700;
    color: $text-primary;
    line-height: 1.1;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    em {
      font-style: normal;
      font-size: 13px;
      font-weight: 500;
      color: $text-muted;
    }
  }

  // Completed = tappable, opens panel
  &__stat--button {
    cursor: pointer;
    border-radius: 12px;
    transition: background 0.15s;

    &:active {
      background: rgba(255, 255, 255, 0.06);
    }
  }

  &__icon--completed {
    color: #22c55e;
  }

  &__icon--scheduled {
    color: #3b82f6;
  }

  // NEW = action needed, Tradel orange; pulses while there are orders waiting
  &__stat--new {
    .merchant-statsbar__value {
      color: $text-primary;
    }

    .merchant-statsbar__dot {
      background: rgba(255, 255, 255, 0.25);
      box-shadow: none;
    }
  }

  &__stat--alert {
    .merchant-statsbar__value {
      color: $tradel-orange;
    }

    .merchant-statsbar__dot {
      background: $tradel-orange;
      box-shadow: 0 0 6px rgba(255, 141, 47, 0.7);
      animation: statsbar-pulse 1.6s ease-in-out infinite;
    }
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: $tradel-orange;
  }

  // ── Settings action (right) ───────────────────────────────────────────
  &__settings-toggle {
    align-self: center;
    min-height: 44px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.04);
    color: $text-primary;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:active {
      background: rgba(255, 255, 255, 0.1);
    }
  }
}
</style>
